import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Inline subscribe form that POSTs first_name + email to our own
 * /api/subscribe endpoint, which then pushes the contact into Systeme.io
 * via their REST API (with the right tags). This replaces the previous
 * iframe-POST-to-embedded-form trick which was silently routing into a
 * legacy donielberry.com funnel.
 *
 * Pass a `source` so Dan can see in Systeme where each subscriber came
 * from (hero / footer / notes-page / etc).
 */
export default function SubscribeForm({
  variant = "footer",
  testIdPrefix = "subscribe",
  buttonLabel = "Subscribe",
  source,
}) {
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const isCompact = variant === "compact";
  const labelColor = "#F2D589";
  const inputBase =
    "h-11 w-full rounded-md bg-white/[0.06] border border-white/15 text-white placeholder:text-white/40 px-3 outline-none focus:border-[#F2D589] transition-colors";

  const submit = async (e) => {
    e.preventDefault();
    if (!firstName.trim() || !email.trim()) {
      toast.error("Need a first name and an email.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/subscribe`, {
        first_name: firstName.trim(),
        email: email.trim().toLowerCase(),
        source: source || testIdPrefix || "general",
      });
      setDone(true);
      toast.success("You're in. Welcome.");
    } catch (err) {
      toast.error("Hmm, that didn't go through. Try again in a moment.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div
        className="rounded-full border border-white/10 bg-white/[0.04] px-4 py-4"
        data-testid={`${testIdPrefix}-done`}
      >
        <p
          className="text-[10px] uppercase tracking-[0.32em] mb-1"
          style={{ color: labelColor }}
        >
          You&apos;re in
        </p>
        <p
          className="text-lg sm:text-xl"
          style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
        >
          Check your inbox. First dispatch coming soon.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid={`${testIdPrefix}-form`}
      className={isCompact ? "flex flex-col gap-2" : "flex flex-col sm:flex-row gap-2"}
    >
      <input
        type="text"
        required
        placeholder="First name"
        value={firstName}
        onChange={(e) => setFirstName(e.target.value)}
        autoComplete="given-name"
        data-testid={`${testIdPrefix}-first-name`}
        className={inputBase}
        style={{ fontFamily: "'Courier Prime', monospace" }}
      />
      <input
        type="email"
        required
        placeholder="Email address"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        autoComplete="email"
        data-testid={`${testIdPrefix}-email`}
        className={inputBase}
        style={{ fontFamily: "'Courier Prime', monospace" }}
      />
      <button
        type="submit"
        disabled={submitting}
        data-testid={`${testIdPrefix}-submit`}
        className="h-11 rounded-full px-5 whitespace-nowrap"
        style={{
          backgroundColor: "transparent",
          color: "#F2D589",
          border: "1px solid #F2D589",
          fontFamily: "'Courier Prime', monospace",
          letterSpacing: "0.2em",
          textTransform: "uppercase",
          fontWeight: 600,
        }}
      >
        {submitting ? "Sending…" : buttonLabel}
      </button>
    </form>
  );
}
